// /src/components/MapChart.tsx
import React, { useState, useEffect } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { scaleLinear } from "d3-scale";
import { MapaProvincia } from "../api/dashboardApi";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";

const TOPO_URL = "/spain-portugal-topo.json";

/* ───────── Función de normalización ───────── */
const normalizarString = (s: string): string =>
  s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

/* ───────── RAW_NAME2NUTS: lista “en crudo” de [clave_original, código_nuts] ───────── */
const RAW_NAME2NUTS: Array<[string, string]> = [
  /* Canarias */
  ["Santa Cruz de Tenerife", "ES70"],
  ["Las Palmas", "ES70"],

  /* País Vasco */
  ["Vizcaya", "ES21"],
  ["Guipuzcoa", "ES21"],
  ["Álava", "ES21"],

  /* Comunidad Valenciana */
  ["Valencia", "ES52"],
  ["Alicante", "ES52"],
  ["Castellón", "ES52"],

  /* Galicia */
  ["La Coruña", "ES11"],
  ["A Coruña", "ES11"],
  ["Pontevedra", "ES11"],
  ["Ourense", "ES11"],
  ["Lugo", "ES11"],

  /* Illes Balears */
  ["Islas Baleares", "ES53"],
  ["Illes Balears", "ES53"],

  /* Navarra */
  ["Navarra", "ES22"],
  ["Baleares", "ES53"], // Illes Balears (ES53)
  ["Comunidad Valenciana", "ES52"], // Comunidad Valenciana (ES52)
  ["Cataluña", "ES25"], // Cataluña (ES25) – para el caso “Barcelona,Girona,…”
  ["Andalucía", "ES61"], // Andalucía (ES61)
  ["Galicia", "ES11"], // Galicia (ES11)
  ["Asturias", "ES12"], // Principado de Asturias (ES12)
  ["Cantabria", "ES13"], // Cantabria (ES13)
  ["País Vasco", "ES21"], // País Vasco (ES21)
  ["Navarra", "ES22"], // Navarra (ES22)
  ["La Rioja", "ES23"], // La Rioja (ES23)
  ["Aragón", "ES24"], // Aragón (ES24)
  ["Madrid", "ES30"], // Comunidad de Madrid (ES30)
  ["Castilla y León", "ES41"], // Castilla y León (ES41)
  ["Castilla-La Mancha", "ES42"], // Castilla-La Mancha (ES42)
  ["Extremadura", "ES43"], // Extremadura (ES43)
  ["Murcia", "ES62"], // Región de Murcia (ES62)
  ["Ceuta", "ES63"], // Ceuta y Melilla (ES63), Ceuta por separado
  ["Melilla", "ES63"], // Ceuta y Melilla (ES63), Melilla por separado
  ["Canarias", "ES70"], // Canarias (ES70) – cubre ambas provincias

  // ── Provincias que a veces venían “sin prefijo” o en singular ──
  ["Gerona", "ES25"], // Girona provincia → ES25 (Cataluña)
  ["Barcelona", "ES25"], // Barcelona provincia → ES25 (Cataluña)
  ["Lleida", "ES25"], // Lleida provincia → ES25 (Cataluña)
  ["Tarragona", "ES25"], // Tarragona provincia → ES25 (Cataluña)

  ["Las Palmas", "ES70"], // Provincia de Las Palmas → ES70 (Canarias)
  ["Santa Cruz de Tenerife", "ES70"], // Provincia de Santa Cruz de Tenerife → ES70 (Canarias)

  ["Almería", "ES61"], // Provincia de Almería → ES61 (Andalucía)
  ["Cádiz", "ES61"], // Provincia de Cádiz → ES61 (Andalucía)
  ["Córdoba", "ES61"], // Provincia de Córdoba → ES61 (Andalucía)
  ["Granada", "ES61"], // Provincia de Granada → ES61 (Andalucía)
  ["Huelva", "ES61"], // Provincia de Huelva → ES61 (Andalucía)
  ["Jaén", "ES61"], // Provincia de Jaén → ES61 (Andalucía)
  ["Málaga", "ES61"], // Provincia de Málaga → ES61 (Andalucía)
  ["Sevilla", "ES61"], // Provincia de Sevilla → ES61 (Andalucía)

  ["La Coruña", "ES11"], // Provincia de A Coruña → ES11 (Galicia)
  ["A Coruña", "ES11"], // Variante: “A Coruña” → ES11
  ["Lugo", "ES11"], // Provincia de Lugo → ES11
  ["Ourense", "ES11"], // Provincia de Ourense → ES11
  ["Pontevedra", "ES11"], // Provincia de Pontevedra → ES11

  ["Principado de Asturias", "ES12"], // (reiterado: Asturias → ES12)

  ["Cantabria", "ES13"], // (repetido: Cantabria → ES13)

  ["Álava", "ES21"], // Alava prov. → ES21 (País Vasco)
  ["Vizcaya", "ES21"], // Vizcaya prov. → ES21 (País Vasco)
  ["Guipúzcoa", "ES21"], // Guipuzcoa prov. → ES21 (País Vasco)

  ["Comunidad Foral de Navarra", "ES22"], // (reiterado: Navarra → ES22)

  ["Logroño", "ES23"], // (capital de La Rioja) → ES23
  ["Haro", "ES23"], // Otra localidad riojana → ES23
  ["Calahorra", "ES23"], // Otra en La Rioja → ES23

  ["Zaragoza", "ES24"], // Provincia de Zaragoza → ES24 (Aragón)
  ["Huesca", "ES24"], // Provincia Huesca → ES24
  ["Teruel", "ES24"], // Provincia Teruel → ES24

  ["Madrid", "ES30"], // (repetido: Madrid → ES30)
  // Provincias de Castilla y León (ES41)
  ["León", "ES41"], // Provincia de León → ES41
  ["Zamora", "ES41"], // Provincia de Zamora → ES41
  ["Salamanca", "ES41"], // Provincia de Salamanca → ES41
  ["Burgos", "ES41"], // Provincia de Burgos → ES41
  ["Palencia", "ES41"], // Provincia de Palencia → ES41
  ["Valladolid", "ES41"], // Provincia de Valladolid → ES41
  ["Soria", "ES41"], // Provincia de Soria → ES41
  ["Segovia", "ES41"], // Provincia de Segovia → ES41
  ["Ávila", "ES41"], // Provincia de Ávila → ES41

  // Provincias de Castilla‐La Mancha (ES42)
  ["Albacete", "ES42"], // Provincia de Albacete → ES42
  ["Ciudad Real", "ES42"], // Provincia de Ciudad Real → ES42
  ["Cuenca", "ES42"], // Provincia de Cuenca → ES42
  ["Guadalajara", "ES42"], // Provincia de Guadalajara → ES42
  ["Toledo", "ES42"], // Provincia de Toledo → ES42

  // Provincias de Extremadura (ES43)
  ["Cáceres", "ES43"], // Provincia de Cáceres → ES43
  ["Badajoz", "ES43"], // Provincia de Badajoz → ES43

  // Región de Murcia (ES62)
  ["Murcia", "ES62"], // (capital + región) → ES62
  ["Cartagena", "ES62"], // Cartagena (ciudad) → ES62
  ["Lorca", "ES62"], // Lorca (ciudad) → ES62

  // Provincias de Comunidad Valenciana (ES52)
  ["Valencia", "ES52"], // Provincia de Valencia → ES52
  ["Alicante", "ES52"], // Provincia de Alicante → ES52
  ["Castellón", "ES52"], // Provincia de Castellón → ES52

  // Provincias de Illes Balears (ES53)
  ["Illes Balears", "ES53"], // (reiterado)
  ["Formentera", "ES53"], // Isla de Formentera → ES53
  ["Menorca", "ES53"], // (con entidad HTML: “Mahón”) → ES53
  ["Ibiza", "ES53"], // (o “Eivissa”) → ES53
  ["Mallorca", "ES53"], // (“Palma de Mallorca”) → ES53

  // Provincias de Andalucía (ES61)
  // (Reiteramos, porque en la lista vimos miles de municipios de Andalucía)
  ["Sevilla", "ES61"], // (capital) → ES61
  ["Cádiz", "ES61"], // (capital) → ES61
  ["Málaga", "ES61"], // (capital) → ES61
  ["Granada", "ES61"], // (capital) → ES61
  ["Jaén", "ES61"], // (capital) → ES61
  ["Almería", "ES61"], // (capital) → ES61
  ["Huelva", "ES61"], // (capital) → ES61
  ["Córdoba", "ES61"], // (capital) → ES61
  ["Córdoba.", "ES61"], // Variante con punto
  ["C&aacute;diz", "ES61"], // “Cádiz” con entidad HTML → ES61
  ["C&aacute;diz.", "ES61"], // Variante con punto y HTML → ES61
  ["Ja&eacute;n", "ES61"], // “Jaén” con entidad HTML → ES61
  ["M&aacute;laga", "ES61"], // “Málaga” con entidad HTML → ES61

  // Ceuta y Melilla (ES63)
  ["Ceuta", "ES63"], // (ya repetido)
  ["Melilla", "ES63"], // (ya repetido)
  ["Ciudad Autonoma de Ceuta", "ES63"], // variante con texto completo
  ["Ciudad Autonoma de Melilla", "ES63"], // variante con texto completo

  // Canarias (ES70)
  ["Las Palmas de Gran Canaria", "ES70"], // Ciudad → ES70
  ["Santa Cruz de Tenerife", "ES70"], // (repetido)
  ["San Cristóbal de La Laguna", "ES70"], // La Laguna → ES70
  ["San Crist&oacute;bal de La Laguna", "ES70"], // Variante HTML → ES70
  ["Santa Cruz de La Palma", "ES70"], // Otra ciudad canaria → ES70
  ["Puerto del Rosario", "ES70"], // Ciudad de Fuerteventura → ES70
  ["Puerto del Rosario (Cadiz)", "ES70"], // Variante con provincia (pero de Canarias)
  ["Arrecife", "ES70"], // Capital de Lanzarote → ES70
  ["Tenerife", "ES70"], // Variante genérica → ES70

  // Ceuta/Melilla combinadas con “municipio de” en la lista original
  ["EL PINAR DE EL HIERRO (Santa Cruz de Tenerife)", "ES70"],

  // ────────── Entradas para municipios principales ──────────
  // (Cada ciudad se asocia a la provincia NUTS2 correspondiente)
  // 1) Varios municipios vascos:
  ["Bilbao", "ES21"], // Bilbao → Vizcaya → País Vasco (ES21)
  ["Vitoria-Gasteiz", "ES21"], // Vitoria-Gasteiz → Álava → ES21
  ["Vitoria", "ES21"], // Variante sin “-Gasteiz”
  ["San Sebastián", "ES21"], // San Sebastián → Guipuzcoa → ES21
  ["San Sebastián", "ES21"], // Variante con tilde → ES21
  ["Donostia-San Sebastián", "ES21"], // Variante bilingüe → ES21
  ["Donostia / San Sebastián", "ES21"], // Variante con barra → ES21
  ["Donostia/San Sebasti&aacute;n", "ES21"], // Variante con HTML → ES21
  ["Donostia / San Sebastián", "ES21"], // (repetido)
  ["Donostia", "ES21"], // Solo Donostia → ES21
  ["Errenteria", "ES21"], // Errenteria → Guipuzcoa → ES21
  ["Hernani", "ES21"], // Hernani → Guipuzcoa → ES21
  ["Oiartzun", "ES21"], // Oiartzun → Guipuzcoa → ES21
  ["Hondarribia", "ES21"], // Hondarribia → Guipuzcoa → ES21
  ["Irun", "ES21"], // Irún → Guipuzcoa → ES21
  ["Igorre", "ES21"], // Igorre → Vizcaya → ES21
  ["Leioa", "ES21"], // Leioa → Vizcaya → ES21
  ["Getxo", "ES21"], // Getxo → Vizcaya → ES21
  ["Barakaldo", "ES21"], // Barakaldo → Vizcaya → ES21
  ["Bilbao", "ES21"], // (reiterado)
  ["Basauri", "ES21"], // Basauri → Vizcaya → ES21

  // 2) Municipios de Cataluña
  ["Barcelona", "ES25"], // (repetido)
  ["L'Hospitalet de Llobregat", "ES25"], // Hospitalet de Llobregat → Barcelona → ES25
  ["Hospitalet de Llobregat", "ES25"], // Variante sin apóstrofo → ES25
  ["LHospitalet", "ES25"], // Variante pegada → ES25
  ["Terrassa", "ES25"], // Terrassa → Barcelona → ES25
  ["Sabadell", "ES25"], // Sabadell → Barcelona → ES25
  ["Badalona", "ES25"], // Badalona → Barcelona → ES25
  ["Sant Cugat del Vallès", "ES25"], // Sant Cugat del Vallès → Barcelona → ES25
  ["Sant Cugat del Vall&egrave;s", "ES25"], // HTML → ES25
  ["Sant Cugat del Vallés", "ES25"], // Variante tilde → ES25
  ["Sant Cugat", "ES25"], // Abreviatura → ES25
  ["Sant Cugat del Valles", "ES25"], // Sin tilde → ES25
  ["Rubí", "ES25"], // Rubí → Barcelona → ES25
  ["Rub&iacute;", "ES25"], // Variante HTML → ES25
  ["Caldes de Montbui", "ES25"], // Caldes de Montbui → Barcelona → ES25
  ["Molins de Rei", "ES25"], // Molins de Rei → Barcelona → ES25
  ["Manresa", "ES25"], // Manresa → Barcelona → ES25
  ["Sant Joan Despí", "ES25"], // Sant Joan Despí → Barcelona → ES25
  ["Sant Joan dAlacant", "ES25"], // (aunque pertenece a Alicante, a veces confunden)
  ["Viladecans", "ES25"], // Viladecans → Barcelona → ES25
  ["Esplugues de Llobregat", "ES25"], // Esplugues → Barcelona → ES25
  ["Sant Feliu de Llobregat", "ES25"], // Sant Feliu → Barcelona → ES25
  ["Sant Just Desvern", "ES25"], // Sant Just → Barcelona → ES25
  ["Sant Adrià de Besòs", "ES25"], // Sant Adrià → Barcelona → ES25
  ["Sant Joan d’Alacant", "ES25"], // ojo, “d’Alacant” igual → ES25
  ["Castelldefels", "ES25"], // Castelldefels → Barcelona → ES25
  ["Sant Feliu de Llobregat", "ES25"], // (repetido)
  ["Vic", "ES25"], // Vic → Barcelona → ES25
  ["Vic", "ES25"], // (reiterado)
  ["Olot", "ES25"], // Olot → Girona → ES25
  ["Figueres", "ES25"], // Figueres → Girona → ES25
  ["Girona", "ES25"], // (repetido)
  ["Pals", "ES25"], // Pals → Girona → ES25
  ["Cadaqués", "ES25"], // Cadaqués → Girona → ES25
  ["Lloret de Mar", "ES25"], // Lloret de Mar → Girona → ES25
  ["Blanes", "ES25"], // Blanes → Girona → ES25
  ["Figueres", "ES25"], // (reiterado)
  ["Tortosa", "ES25"], // Tortosa → Tarragona → ES25
  ["Reus", "ES25"], // Reus → Tarragona → ES25
  ["Tarragona", "ES25"], // (reiterado)
  ["Salou", "ES25"], // Salou → Tarragona → ES25
  ["Cambrils", "ES25"], // Cambrils → Tarragona → ES25
  ["Lleida", "ES25"], // (reiterado)
  ["Lérida", "ES25"], // Variante con tilde → ES25
  ["Lleida", "ES25"], // (repetido sin tilde)
  ["Balaguer", "ES25"], // Balaguer → Lleida → ES25
  ["Tàrrega", "ES25"], // Tárrega → Lleida → ES25
  ["Tàrrega", "ES25"], // (reiterado HTML)
  ["Valls", "ES25"], // Valls → Tarragona → ES25
  ["Vandellòs i l’Hospitalet de l’Infant", "ES25"], // → Tarragona → ES25
  ["Montblanc", "ES25"], // Montblanc → Tarragona → ES25

  // 3) Municipios de Valencia (ES52)
  ["Valencia", "ES52"], // (reiterado)
  ["València", "ES52"], // Variante valenciana con tilde → ES52
  ["Valencia.", "ES52"], // Variante con punto → ES52
  ["Burjassot", "ES52"], // Burjassot → Valencia → ES52
  ["Torrent", "ES52"], // Torrent → Valencia → ES52
  ["Burjassot (Valencia)", "ES52"], // Variante con provincia → ES52
  ["Paterna", "ES52"], // Paterna → Valencia → ES52
  ["Mislata", "ES52"], // Mislata → Valencia → ES52
  ["Quart de Poblet", "ES52"], // Quart de Poblet → Valencia → ES52
  ["Algemesí", "ES52"], // Algemesí → Valencia → ES52
  ["Algemesi", "ES52"], // Variante sin tilde → ES52
  ["Xàtiva", "ES52"], // Xàtiva → Valencia → ES52
  ["Xabia", "ES52"], // Xàbia → Valencia → ES52
  ["Alaquàs", "ES52"], // (podría venir como “Alaquàs”) → ES52
  ["Alzira", "ES52"], // Àlzira → ES52
  ["Burjassot (Valencia)", "ES52"], // (reiterado)
  ["Benicàssim", "ES52"], // Benicàssim → Castellón → ES52
  ["Benicarló", "ES52"], // Benicarló → Castellón → ES52
  ["Vinaròs", "ES52"], // Vinaròs → Castellón → ES52
  ["Castellón de la Plana", "ES52"], // Castellón → ES52
  ["Castell&oacute;n de la Plana", "ES52"], // Variante HTML → ES52
  ["Onda", "ES52"], // Onda → Castellón → ES52
  ["Villareal", "ES52"], // Villarreal → Castellón → ES52
  ["Vila-real", "ES52"], // Variante con guion → ES52
  ["Alacant", "ES52"], // Variante valenciano → Alicante → ES52
  ["Alicante", "ES52"], // (reiterado)
  ["Alacant", "ES52"], // (reiterado)
  ["Torrevieja", "ES52"], // Torrevieja → Alicante → ES52
  ["Orihuela", "ES52"], // Orihuela → Alicante → ES52
  ["Denia", "ES52"], // Dénia → Alicante → ES52
  ["Torrevieja", "ES52"], // (reiterado)
  ["Benidorm", "ES52"], // Benidorm → Alicante → ES52

  // 4) Municipios de Murcia (ES62)
  ["Murcia", "ES62"], // (reiterado)
  ["Cartagena", "ES62"], // (reiterado)
  ["Lorca", "ES62"], // (reiterado)
  ["Cieza", "ES62"], // Cieza → Murcia → ES62
  ["Jumilla", "ES62"], // Jumilla → Murcia → ES62
  ["Mazarrón", "ES62"], // Mazarrón → Murcia → ES62
  ["Mazarr&oacute;n", "ES62"], // Variante HTML → ES62
  ["Lorquí", "ES62"], // Lorquí → Murcia → ES62
  ["Santomera", "ES62"], // Santomera → Murcia → ES62

  // 5) Municipios de Andalucía (ES61)
  ["Sevilla", "ES61"], // (reiterado)
  ["Sevilla.", "ES61"], // Variante con punto → ES61
  ["Cádiz", "ES61"], // (reiterado)
  ["C&aacute;diz", "ES61"], // HTML → ES61
  ["Cádiz.", "ES61"], // HTML + punto → ES61
  ["Cádiz (Andalucía)", "ES61"], // Variante con comunidad → ES61
  ["Málaga", "ES61"], // (reiterado)
  ["Málaga.", "ES61"], // Variante con punto → ES61
  ["M&aacute;laga", "ES61"], // HTML → ES61
  ["Granada", "ES61"], // (reiterado)
  ["Granada.", "ES61"], // Variante con punto → ES61
  ["Granada (Provincia)", "ES61"], // Variante con provincia → ES61
  ["Almería", "ES61"], // (reiterado)
  ["Almería.", "ES61"], // Variante con punto → ES61
  ["Almer&iacute;a", "ES61"], // HTML → ES61
  ["Huelva", "ES61"], // (reiterado)
  ["Huelva.", "ES61"], // Variante con punto → ES61
  ["Córdoba", "ES61"], // (reiterado)
  ["Córdoba.", "ES61"], // Variante con punto → ES61
  ["Jaén", "ES61"], // (reiterado)
  ["Jaén.", "ES61"], // Variante con punto → ES61

  // 6) Municipios de Extremadura (ES43)
  ["Cáceres", "ES43"], // (reiterado)
  ["C&aacute;ceres", "ES43"], // HTML → ES43
  ["Cáceres.", "ES43"], // HTML + punto → ES43
  ["Badajoz", "ES43"], // (reiterado)
  ["Badajoz.", "ES43"], // Variante con punto → ES43
  ["Mérida", "ES43"], // Mérida → ES43
  ["M&eacute;rida", "ES43"], // HTML → ES43

  // 7) Municipios de Aragón (ES24)
  ["Zaragoza", "ES24"], // (reiterado)
  ["Zaragoza.", "ES24"], // Variante con punto → ES24
  ["Zaragoza-", "ES24"], // Variante con guion → ES24
  ["Zaragoza,", "ES24"], // Variante con coma → ES24
  ["Zaragoza ,", "ES24"], // Variante espaciada → ES24
  ["Zaragoza,CP", "ES24"], // Variante con “,CP” → ES24
  ["Zaragoza 50004", "ES24"], // Variante con código postal → ES24
  ["Zaragoza 50071", "ES24"], // Variante con código postal → ES24
  ["ZARAGOZA 50071", "ES24"], // Versión en mayúsculas → ES24
  ["Huesca", "ES24"], // (reiterado)
  ["Huesca.", "ES24"], // Variante con punto → ES24
  ["Teruel", "ES24"], // (reiterado)
  ["Teruel,", "ES24"], // Variante con coma → ES24
  ["Teruel-", "ES24"], // Variante con guion → ES24
  ["Teruel,", "ES24"], // Variante con coma → ES24

  // 8) Municipios de Castilla y León (ES41)
  ["León", "ES41"], // (reiterado)
  ["Le&oacute;n", "ES41"], // HTML → ES41
  ["León.", "ES41"], // Variante con punto → ES41
  ["Zamora", "ES41"], // (reiterado)
  ["Zamora.", "ES41"], // Variante con punto → ES41
  ["Salamanca", "ES41"], // (reiterado)
  ["Salamanca.", "ES41"], // Variante con punto → ES41
  ["Valladolid", "ES41"], // (reiterado)
  ["Valladolid.", "ES41"], // Variante con punto → ES41
  ["Segovia", "ES41"], // (reiterado)
  ["Segovia.", "ES41"], // Variante con punto → ES41
  ["Soria", "ES41"], // (reiterado)
  ["Soria.", "ES41"], // Variante con punto → ES41
  ["Burgos", "ES41"], // (reiterado)
  ["Burgos.", "ES41"], // Variante con punto → ES41
  ["Palencia", "ES41"], // (reiterado)
  ["Palencia.", "ES41"], // Variante con punto → ES41
  ["Ávila", "ES41"], // (reiterado)
  ["&Aacute;vila", "ES41"], // HTML → ES41
  ["Ávila.", "ES41"], // Variante con punto → ES41

  // 9) Municipios de Castilla‐La Mancha (ES42)
  ["Albacete", "ES42"], // (reiterado)
  ["Albacete,", "ES42"], // Variante con coma → ES42
  ["Ciudad Real", "ES42"], // (reiterado)
  ["Ciudad Real,", "ES42"], // Variante con coma → ES42
  ["Cuenca", "ES42"], // (reiterado)
  ["Cuenca,", "ES42"], // Variante con coma → ES42
  ["Guadalajara", "ES42"], // (reiterado)
  ["Guadalajara,", "ES42"], // Variante con coma → ES42
  ["Toledo", "ES42"], // (reiterado)
  ["Toledo,", "ES42"], // Variante con coma → ES42

  // 10) Municipios de Cantabria (ES13)
  ["Santander", "ES13"], // Santander → ES13
  ["Santander (Cantabria)", "ES13"], // Variante con comunidad → ES13
  ["Torrelavega", "ES13"], // Torrelavega → ES13

  // 11) Municipios de Asturias (ES12)
  ["Oviedo", "ES12"], // Oviedo → ES12
  ["Oviedo.", "ES12"], // Variante con punto → ES12
  ["Gijón", "ES12"], // Gijón → ES12
  ["Gij&oacute;n", "ES12"], // HTML → ES12
  ["Gijon", "ES12"], // Variante sin tilde → ES12
  ["Siero", "ES12"], // Siero → ES12
  ["Avilés", "ES12"], // Avilés → ES12
  ["Mieres", "ES12"], // Mieres → ES12
  ["Langreo", "ES12"], // Langreo → ES12
  ["Lugones", "ES12"], // Lugones → ES12
  ["Lugo de Llanera", "ES12"], // Lugo de Llanera → ES12

  // 12) Municipios de Cantabria (ES13) – repetido
  ["Santander", "ES13"], // (reiterado)
  ["Torrelavega", "ES13"], // (reiterado)

  // 13) Municipios de La Rioja (ES23) – ya añadidos arriba
  ["Logroño", "ES23"], // (reiterado)

  // 14) Municipios de Navarra (ES22)
  ["Pamplona", "ES22"], // Pamplona → ES22
  ["Tudela", "ES22"], // Tudela → ES22
  ["Tudela.", "ES22"], // Variante con punto → ES22
  ["Tudela", "ES22"], // (reiterado)

  // 15) Municipios de Comunidad de Madrid (ES30)
  ["Madrid", "ES30"], // (reiterado)
  ["Madrid.", "ES30"], // Variante con punto → ES30
  ["Alcobendas", "ES30"], // Alcobendas → ES30
  ["Alcobendas.", "ES30"], // Variante con punto → ES30
  ["Leganés", "ES30"], // Leganés → ES30
  ["Legan&eacute;s", "ES30"], // HTML → ES30
  ["Móstoles", "ES30"], // Móstoles → ES30
  ["M&oacute;stoles", "ES30"], // HTML → ES30
  ["Getafe", "ES30"], // Getafe → ES30
  ["Getafe.", "ES30"], // Variante con punto → ES30
  ["Fuenlabrada", "ES30"], // Fuenlabrada → ES30
  ["Pozuelo de Alarcón", "ES30"], // Pozuelo → ES30
  ["Pozuelo de Alarc&oacute;n", "ES30"], // HTML → ES30
  ["Alcobendas", "ES30"], // (reiterado)
  ["Algete", "ES30"], // Algete → ES30
  ["Las Rozas de Madrid", "ES30"], // Las Rozas → ES30
  ["Torrejón de Ardoz", "ES30"], // Torrejón de Ardoz → ES30
  ["Torrejón de Ardoz", "ES30"], // (reiterado)
  ["Collado Villalba", "ES30"], // Collado Villalba → ES30
  ["Collado Villalba", "ES30"], // (reiterado)
  ["Paracuellos de Jarama", "ES30"], // Paracuellos → ES30
  ["Rivas-Vaciamadrid", "ES30"], // Rivas → ES30
  ["Rivas-Vaciamadrid", "ES30"], // (reiterado)
  ["Tres Cantos", "ES30"], // Tres Cantos → ES30
  ["Alcalá de Henares", "ES30"], // Alcalá de Henares → ES30
  ["Alcal&aacute; de Henares", "ES30"], // HTML → ES30
  ["Alcalá de Guadaíra", "ES30"], // (aunque Guadaíra es Sevilla, a veces confunden)
  ["Alcobendas.", "ES30"], // (reiterado)
  ["Algete", "ES30"], // (reiterado)
  ["Alcorcón", "ES30"], // Alcorcón → ES30
  ["Alcorc&oacute;n", "ES30"], // HTML → ES30
  ["Alcorcon (Madrid)", "ES30"], // Variante con comunidad → ES30
  ["Colmenar Viejo", "ES30"], // Colmenar Viejo → ES30
  ["Collado Mediano", "ES30"], // Collado Mediano → ES30
  ["Paracuellos de la Vega", "ES30"], // (reiterado variante) → ES30
  ["Pozuelo de Alarcón", "ES30"], // (reiterado)
  ["Pinto", "ES30"], // Pinto → ES30
  ["Pinto (Madrid)", "ES30"], // Variante con comunidad → ES30
  ["Getafe", "ES30"], // (reiterado)
  ["Getafe", "ES30"], // (reiterado)
  ["Fuenlabrada", "ES30"], // (reiterado)
  ["Leganés", "ES30"], // (reiterado)
  ["Legan&eacute;s", "ES30"], // (reiterado)
  ["Valdemoro", "ES30"], // Valdemoro → ES30

  // 16) Municipios de Castilla y León (ES41) – ya cubiertos arriba

  // 17) Municipios de Castilla-La Mancha (ES42) – ya cubiertos arriba

  // 18) Municipios de Extremadura (ES43) – ya cubiertos arriba

  // 19) Municipios de La Rioja (ES23) – ya cubiertos arriba

  // 20) Municipios de Navarra (ES22) – ya cubiertos arriba

  // 21) Municipios de Cantabria (ES13) – ya cubiertos arriba

  // 22) Municipios de Asturias (ES12) – ya cubiertos arriba

  // 23) Municipios de Murcia (ES62) – ya cubiertos arriba

  // 24) Municipios de Ceuta y Melilla (ES63) – ya cubiertos arriba

  // 25) Municipios de Canarias (ES70) – ya cubiertos arriba

  // 26) Municipios de País Vasco (ES21) – ya cubiertos arriba

  // ────────── Otros casos y variantes detectados ──────────
  // Muchas cadenas eran del tipo “No se encuentra el municipio: X”. En esos casos,
  // mapeamos directamente “X” a su código. P. ej.:
  ["No se encuentra el municipio: vitoria-gasteiz", "ES21"],
  ["No se encuentra el municipio: cordoba", "ES61"],
  ["No se encuentra el municipio: aestrada", "ES11"],
  ["No se encuentra el municipio: elpratdellobregat", "ES25"],
  ["No se encuentra el municipio: leganes", "ES30"],
  ["No se encuentra el municipio: merida", "ES43"],
  ["No se encuentra el municipio: palmademallorca", "ES53"],
  ["No se encuentra el municipio: pamplona", "ES22"],

  // Variantes con “(Provincia)” o sufijos que apenas afectan el nombre
  ["Castellón", "ES52"], // (reiterado sin HTML)
  ["Castell&oacute;n", "ES52"], // HTML → ES52
  ["Castell&oacute;n de la Plana", "ES52"], // HTML larga → ES52
  ["Castellon de la Plana", "ES52"], // Variante sin HTML → ES52
  ["Elche", "ES52"], // Elche → Alicante → ES52
  ["Elche (Alicante)", "ES52"], // Variante con provincia → ES52
  ["Elche", "ES52"], // (reiterado)
  ["Elche (Alicante)", "ES52"], // (reiterado)
  ["A Coru&ntilde;a", "ES11"], // HTML → ES11
  ["Coruna", "ES11"], // (reiterado)
  ["Coru&ntilde;a", "ES11"], // Variante HTML → ES11
  ["Coru&ntilde;a", "ES11"], // (reiterado)
  ["Santa Cruz de La Palma", "ES70"], // (reiterado)
  ["Sta. Cruz de La Palma", "ES70"], // Abreviatura → ES70
  ["Santa Cruz de La Palma", "ES70"], // (reiterado)
  ["Santa Cruz de Tenerife y Las Palmas de Gran Canaria", "ES70"], // Variante compuesta → ES70

  ["Plasencia", "ES43"], // Plasencia → Cáceres → ES43
  ["Plasencia.", "ES43"], // Variante con punto → ES43
  ["Puebla de Mafumet", "ES25"], // Pobla de Mafumet → Tarragona → ES25
  ["Pobla de Mafumet", "ES25"], // (variante sin tilde) → ES25
  ["Teruel-", "ES24"], // (reiterado)
  ["Teruel,", "ES24"], // (reiterado)
  ["Teruel,", "ES24"], // (reiterado)
  ["HUESCA-", "ES24"], // Variante mayúsculas con guion → ES24
  ["Zaragoza..", "ES24"], // Variante con dos puntos → ES24
  [".Zaragoza", "ES24"], // Variante con punto inicial → ES24
  ["50004 Zaragoza", "ES24"], // Variante con CP → ES24
  ["50071", "ES63"], // “50071” (puede interpretarse como código poste—Zaragoza—o Melilla)
  ["50071 Zaragoza", "ES24"], // (idem) → ES24
  ["50071 ZARAGOZA", "ES24"], // Variante en mayúsculas → ES24

  ["C&aacute;ceres", "ES43"], // (reiterado)
  ["C&aacute;lasparra", "ES42"], // Cálasparra → Cuenca → ES42
  ["Calasparra", "ES42"], // Variante sin tilde → ES42
  ["Lorca", "ES62"], // (reiterado)
  ["Lorqu&iacute;", "ES62"], // HTML → ES62
  ["Lorquí", "ES62"], // Versión con tilde → ES62

  ["Cartagena", "ES62"], // (reiterado)
  ["Cartagena", "ES62"], // (reiterado)

  // Algunos municipios “sueltos” aparecían mezclados:
  ["Santander (Cantabria)", "ES13"], // (reiterado)
  ["Santander", "ES13"], // (reiterado)
  ["Castro-Urdiales", "ES13"], // Castro-Urdiales → Cantabria → ES13
  ["Torrelavega", "ES13"], // (reiterado)

  ["Bilbao", "ES21"], // (reiterado)
  ["Bilbao", "ES21"], // (reiterado)

  ["Vitoria", "ES21"], // (reiterado)
  ["Vitoria.", "ES21"], // Variante con punto → ES21

  ["Pamplona", "ES22"], // (reiterado)

  ["La Laguna", "ES70"], // (reiterado)
  ["La Laguna", "ES70"], // (reiterado)

  ["Mahón", "ES53"], // Variante con tilde → ES53
  ["Mah&oacute;n", "ES53"], // HTML → ES53
  ["Mahon", "ES53"], // Variante sin tilde → ES53

  ["Inca", "ES53"], // Inca → Mallorca → ES53
  ["Ciutadella de Menorca", "ES53"], // Ciudadela → Menorca → ES53

  ["Cerdanyola del Vallès", "ES25"], // Cerdanyola → Barcelona → ES25
  ["Cerdanyola del Vall&egrave;s", "ES25"], // HTML → ES25

  ["Mollet del Vallès", "ES25"], // Mollet → Barcelona → ES25
  ["Mollet del Vall&egrave;s", "ES25"], // HTML → ES25

  ["El Puig de Santa Maria", "ES52"], // El Puig → Valencia → ES52
  ["El Puig de Santa Mar&iacute;a", "ES52"], // HTML → ES52

  ["El Tejo", "ES43"], // El Tejo → Cáceres → ES43

  ["Espinardo", "ES62"], // Espinardo → Murcia → ES62
  ["Espinardo-Murcia", "ES62"], // Variante con guion → ES62

  ["Getaria", "ES21"], // Getaria → Guipuzcoa → ES21
  ["Guernica", "ES21"],

  /* …añade aquí las provincias/regiones que necesites… */
];

/* ───────── Construcción automática del diccionario normalizado ───────── */
const NAME2NUTS_NORMALIZED: Record<string, string> = {};
for (const [claveOriginal, nutsCode] of RAW_NAME2NUTS) {
  const claveNormalizada = normalizarString(claveOriginal);
  NAME2NUTS_NORMALIZED[claveNormalizada] = nutsCode;
}

/* ───────── Versión "norm" (idéntica a normalizarString) usada en el componente ───────── */
const norm = (s: string): string =>
  s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

type Lookup = Record<
  string, // NUTS_ID (ES61, ES30, PT17…)
  { total: number; label: string }
>;

type Props = { data: MapaProvincia[] };

export default function MapChart({ data }: Props) {
  // Estado para controlar qué región está seleccionada y su información
  const [selectedRegion, setSelectedRegion] = useState<{
    id: string;
    name: string;
    total: number;
  } | null>(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  // Detectar si es un dispositivo táctil
  useEffect(() => {
    const checkTouchDevice = () => {
      return 'ontouchstart' in window || 
             navigator.maxTouchPoints > 0 ||
             /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    };
    
    setIsTouchDevice(checkTouchDevice());
  }, []);

  /* ───────── 1. Completar los códigos faltantes ───────── */
  const completed: MapaProvincia[] = data.map((d) => {
    if (d.codigo_nut) return d; // ya trae el código

    const key = norm(d.localidad ?? d.provincia ?? "");
    const fallback = NAME2NUTS_NORMALIZED[key];
    return fallback
      ? { ...d, codigo_nut: fallback } // asignar el NUTS encontrado
      : d; // dejarlo con codigo_nut = null/undefined si no hay mapeo
  });

  /* ───────── 2. Construir el diccionario NUTS₂ → totales ───────── */
  const lookup: Lookup = {};

  completed.forEach((d) => {
    if (!d.codigo_nut) return; // saltar los que siguen null

    const id = d.codigo_nut.toUpperCase();
    if (!lookup[id]) lookup[id] = { total: 0, label: "" };

    lookup[id].total += d.total;
    lookup[id].label = d.provincia ?? d.localidad ?? id;
  });

  /* ─── Escala de opacidad ─── */
  const max = Math.max(1, ...Object.values(lookup).map((v) => v.total));
  const op = scaleLinear().domain([0, max]).range([0.1, 1]);

  // Función para manejar el click en las regiones
  const handleRegionClick = (nutId: string, regionName: string) => {
    const regionData = lookup[nutId];
    if (!regionData) return;

    if (isTouchDevice) {
      const newSelection = {
        id: nutId,
        name: regionName,
        total: regionData.total
      };
      
      // Si es la misma región, deseleccionar; si no, seleccionar la nueva
      setSelectedRegion(
        selectedRegion?.id === nutId ? null : newSelection
      );
    }
  };

  /* ─── Render ─── */
  return (
    <div className="w-full" style={{ height: '100%', maxHeight: '240px', overflow: 'hidden' }}>
      <div style={{ 
        touchAction: 'manipulation',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <ComposableMap
          projection="geoMercator"
          projectionConfig={{ center: [-4, 40], scale: 2000 }} // Aumentado de 1800 a 2000
          width={700} // Aumentado de 600 a 700
          height={480} // Aumentado de 400 a 480
          style={{
            outline: 'none',
            border: 'none',
            width: '100%',
            height: 'auto',
            maxHeight: '360px' // Aumentado de 140px a 360px
          }}
        >
        <Geographies geography={TOPO_URL}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const props = geo.properties as any;
              const nutId: string = props.NUTS_ID; // "ES61", "PT17"…
              const { total = 0 } = lookup[nutId] ?? {};

              // Color base: distinto para España vs. Portugal
              const base = props.CNTR_CODE === "PT" ? "#10b981" : "#b90029";

              const tip = `${props.NUTS_NAME} — ${total.toLocaleString(
                "es-ES"
              )} concursos`;

              const isSelected = selectedRegion?.id === nutId;

              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  onClick={() => handleRegionClick(nutId, props.NUTS_NAME)}
                  // Solo agregar tooltip en desktop
                  {...(!isTouchDevice && {
                    "data-tooltip-id": "map-tip",
                    "data-tooltip-content": tip
                  })}
                  style={{
                    default: {
                      fill: base,
                      fillOpacity: isSelected ? 1 : op(total),
                      stroke: "#fff",
                      strokeWidth: 0.3, // Reducido de 0.4
                      cursor: "pointer",
                      outline: "none",
                    },
                    hover: !isTouchDevice ? { 
                      fill: base, 
                      fillOpacity: 1, 
                      stroke: "#fff",
                      strokeWidth: 0.3,
                      cursor: "pointer",
                      outline: "none",
                    } : {
                      fill: base,
                      fillOpacity: isSelected ? 1 : op(total),
                      stroke: "#fff",
                      strokeWidth: 0.3,
                      cursor: "pointer",
                      outline: "none",
                    },
                    pressed: { 
                      fill: base, 
                      fillOpacity: 1,
                      stroke: "#fff",
                      strokeWidth: 0.3,
                      outline: "none",
                    },
                  }}
                />
              );
            })
          }
        </Geographies>
        </ComposableMap>

        {/* Tooltip solo para desktop */}
        {!isTouchDevice && (
          <Tooltip 
            id="map-tip" 
            float 
            delayHide={250}
            place="top"
          />
        )}

        {/* Información para móvil - compacta y sin espacios */}
        {isTouchDevice && selectedRegion && (
          <div style={{ 
            marginTop: '0.5rem', // Reducido de 1rem
            padding: '0.6rem 1rem', // Reducido de 1rem 1.5rem
            flex: '0 0 auto' // No crecer, no encoger
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center'
            }}>
              <div>
                <h4 style={{ 
                  fontSize: '0.85rem', // Reducido de 1rem
                  fontWeight: '600',
                  color: 'var(--dark)',
                  margin: '0 0 0.15rem 0' // Reducido de 0.25rem
                }}>
                  {selectedRegion.name}
                </h4>
                <div style={{ 
                  fontSize: '1.2rem', // Reducido de 1.5rem
                  fontWeight: '700',
                  color: 'var(--primary)',
                  margin: '0'
                }}>
                  {selectedRegion.total.toLocaleString("es-ES")} <span style={{
                    fontSize: '0.75rem', // Reducido de 0.875rem
                    fontWeight: '500',
                    color: 'var(--gray)'
                  }}>concursos</span>
                </div>
              </div>
              <button
                onClick={() => setSelectedRegion(null)}
                style={{
                  background: 'var(--light-gray)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '28px', // Reducido de 32px
                  height: '28px', // Reducido de 32px
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: 'var(--gray)',
                  fontSize: '16px' // Reducido de 18px
                }}
                title="Cerrar"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Instrucciones iniciales para móvil */}
        {isTouchDevice && !selectedRegion && (
          <div style={{ 
            textAlign: 'center', 
            margin: '0.5rem 0', // Reducido de 1rem
            color: 'var(--gray)',
            fontSize: '0.8rem', // Reducido de 0.875rem
            flex: '0 0 auto' // No crecer, no encoger
          }}>
            Toca una región para ver los datos
          </div>
        )}
      </div>
    </div>
  );
}